// NoteCard - Display a single personal note (Enhanced Version)

import React, { useState } from 'react';
import { Star, Calendar, Edit2, Trash2, MapPin, Camera, Eye, ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { PlaceNote, PhotoMetadata } from '../../types';
import { formatVisitDate } from '../../services/notesService';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { Timestamp } from '@firebase/firestore';
import { getMoodByValue } from '../../constants/moods';

interface NoteCardProps {
  note: PlaceNote;
  onEdit?: () => void;
  onDelete?: () => void;
  showPlaceInfo?: boolean;
  onNavigateToPlace?: (placeId: string) => void;
}

// Lightbox component for viewing photos
function PhotoLightbox({
  photos,
  initialIndex,
  onClose,
}: {
  photos: PhotoMetadata[];
  initialIndex: number;
  onClose: () => void;
}) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') handlePrev();
    if (e.key === 'ArrowRight') handleNext();
    if (e.key === 'Escape') onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 p-2 text-white/80 hover:text-white transition"
      >
        <X className="w-8 h-8" />
      </button>

      {photos.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePrev();
            }}
            className="absolute left-4 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            className="absolute right-4 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      <img
        src={photos[currentIndex].url}
        alt={`Foto ${currentIndex + 1}`}
        className="max-h-[90vh] max-w-[90vw] object-contain"
        onClick={(e) => e.stopPropagation()}
      />

      {photos.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {photos.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(idx);
              }}
              className={`w-2 h-2 rounded-full transition ${
                idx === currentIndex ? 'bg-white' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function NoteCard({
  note,
  onEdit,
  onDelete,
  showPlaceInfo = true,
  onNavigateToPlace,
}: NoteCardProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const formatDate = (date: any) => {
    if (!date) return '';
    const d = date instanceof Timestamp ? date.toDate() : new Date(date);
    return formatDistanceToNow(d, { addSuffix: true, locale: de });
  };

  // Get moods (prefer new moods array, fallback to legacy mood)
  const moods = note.moods?.length ? note.moods : note.mood ? [note.mood] : [];

  // Get photos
  const photos = note.photoMetadata || [];

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition">
        {/* Header with Place Info */}
        {showPlaceInfo && (
          <div
            className={`px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between ${
              onNavigateToPlace ? 'cursor-pointer hover:bg-gray-100' : ''
            }`}
            onClick={() => onNavigateToPlace?.(note.placeId)}
          >
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-indigo-600" />
              <span className="font-medium text-gray-900">{note.placeName}</span>
            </div>
            {/* Public Badge */}
            {note.isPublic && (
              <div className="flex items-center gap-1 text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                <Eye className="w-3 h-3" />
                <span>Öffentlich</span>
              </div>
            )}
          </div>
        )}

        {/* Photo Gallery */}
        {photos.length > 0 && (
          <div className="relative">
            {photos.length === 1 ? (
              // Single photo
              <div
                className="aspect-video cursor-pointer"
                onClick={() => setLightboxIndex(0)}
              >
                <img
                  src={photos[0].thumbnailUrl || photos[0].url}
                  alt="Foto"
                  className="w-full h-full object-cover"
                />
              </div>
            ) : photos.length === 2 ? (
              // Two photos side by side
              <div className="grid grid-cols-2 gap-0.5">
                {photos.slice(0, 2).map((photo, idx) => (
                  <div
                    key={idx}
                    className="aspect-square cursor-pointer"
                    onClick={() => setLightboxIndex(idx)}
                  >
                    <img
                      src={photo.thumbnailUrl || photo.url}
                      alt={`Foto ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            ) : (
              // Three or more photos
              <div className="grid grid-cols-3 gap-0.5">
                {photos.slice(0, 3).map((photo, idx) => (
                  <div
                    key={idx}
                    className="aspect-square cursor-pointer relative"
                    onClick={() => setLightboxIndex(idx)}
                  >
                    <img
                      src={photo.thumbnailUrl || photo.url}
                      alt={`Foto ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {idx === 2 && photos.length > 3 && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-medium">
                        +{photos.length - 3}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {/* Photo count badge */}
            <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
              <Camera className="w-3 h-3" />
              <span>{photos.length}</span>
            </div>
          </div>
        )}

        <div className="p-4">
          {/* Meta Info Row */}
          <div className="flex items-center gap-4 text-sm text-gray-500 mb-3 flex-wrap">
            {/* Visit Date */}
            {note.visitDate && (
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{formatVisitDate(note.visitDate)}</span>
              </div>
            )}

            {/* Personal Rating */}
            {note.personalRating && (
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-3.5 h-3.5 ${
                      star <= note.personalRating!
                        ? 'text-amber-400 fill-amber-400'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Moods */}
          {moods.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {moods.map((moodValue) => {
                const mood = getMoodByValue(moodValue);
                if (!mood) return null;
                return (
                  <span
                    key={moodValue}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs"
                  >
                    <span>{mood.emoji}</span>
                    <span>{mood.label}</span>
                  </span>
                );
              })}
            </div>
          )}

          {/* Note Text */}
          <p className="text-gray-700 whitespace-pre-wrap">{note.text}</p>

          {/* Footer */}
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
            <span className="text-xs text-gray-400">
              {formatDate(note.createdAt)}
            </span>

            {/* Actions */}
            {(onEdit || onDelete) && (
              <div className="flex items-center gap-1">
                {onEdit && (
                  <button
                    onClick={onEdit}
                    className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-500/10 rounded transition"
                    title="Bearbeiten"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={onDelete}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition"
                    title="Löschen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && photos.length > 0 && (
        <PhotoLightbox
          photos={photos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}

export default NoteCard;
